export const CURRENCY_CODES = [
  "AUD",
  "EUR",
  "USD",
  "GBP",
  "NZD",
  "SGD",
] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];

export interface FxSnapshot {
  readonly base: "AUD";
  readonly quote: CurrencyCode;
  readonly rate: number;
  readonly asOf: string;
  readonly source: string;
  readonly fetchedAt: string;
  readonly manual: boolean;
  readonly note?: string;
}

const symbols: Record<CurrencyCode, string> = {
  AUD: "A$",
  EUR: "€",
  USD: "US$",
  GBP: "£",
  NZD: "NZ$",
  SGD: "S$",
};
const isCode = (value: string): value is CurrencyCode =>
  (CURRENCY_CODES as readonly string[]).includes(value);

function validDate(value: string): boolean {
  if (
    !/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:?\d{2})?)?$/.test(
      value,
    )
  )
    return false;
  const parts = value.slice(0, 10).split("-").map(Number);
  const calendar = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
  if (
    calendar.getUTCFullYear() !== parts[0] ||
    calendar.getUTCMonth() !== parts[1] - 1 ||
    calendar.getUTCDate() !== parts[2]
  )
    return false;
  const d = new Date(value);
  if (!Number.isFinite(d.getTime())) return false;
  return true;
}

export function validateFxSnapshot(input: FxSnapshot): FxSnapshot {
  if (
    input.base !== "AUD" ||
    !isCode(input.quote) ||
    !Number.isFinite(input.rate) ||
    input.rate <= 0 ||
    !input.source ||
    typeof input.manual !== "boolean"
  )
    throw new Error("Invalid FX snapshot");
  if (!validDate(input.asOf) || !validDate(input.fetchedAt))
    throw new Error("Invalid FX snapshot date");
  if (input.quote === "AUD" && input.rate !== 1)
    throw new Error("AUD snapshot rate must be 1");
  return input;
}

export function freezeFxSnapshot(input: FxSnapshot): FxSnapshot {
  const copy: FxSnapshot = {
    base: "AUD",
    quote: input.quote,
    rate: input.rate,
    asOf: input.asOf,
    source: input.source,
    fetchedAt: input.fetchedAt,
    manual: input.manual,
    ...(input.note === undefined ? {} : { note: input.note }),
  };
  validateFxSnapshot(copy);
  return Object.freeze(copy);
}

export const createManualFxSnapshot = (
  quote: CurrencyCode,
  rate: number,
  asOf: string,
  note?: string,
  fetchedAt = new Date().toISOString(),
): FxSnapshot =>
  freezeFxSnapshot({
    base: "AUD",
    quote,
    rate,
    asOf,
    source: "manual",
    fetchedAt,
    manual: true,
    ...(note === undefined ? {} : { note }),
  });

export function convertAud(amount: number, snapshot: FxSnapshot): number {
  validateFxSnapshot(snapshot);
  if (!Number.isFinite(amount)) throw new Error("Amount must be finite");
  return amount * snapshot.rate;
}

export function formatReportingCurrency(
  amount: number,
  snapshot: FxSnapshot,
  compact = false,
): string {
  const converted = convertAud(amount, snapshot);
  const locale = "en-AU";
  const maximumFractionDigits = compact ? 1 : 2;
  if (compact && Math.abs(converted) >= 1_000_000)
    return `${symbols[snapshot.quote]}${(converted / 1_000_000).toFixed(1)}M`;
  if (compact && Math.abs(converted) >= 1_000)
    return `${symbols[snapshot.quote]}${(converted / 1_000).toFixed(1)}K`;
  const number = new Intl.NumberFormat(locale, {
    maximumFractionDigits,
    minimumFractionDigits: maximumFractionDigits,
  }).format(converted);
  return `${symbols[snapshot.quote]}${number}`;
}

export async function fetchLatestFx(
  quote: CurrencyCode,
  signal?: AbortSignal,
): Promise<FxSnapshot> {
  if (quote === "AUD")
    return freezeFxSnapshot({
      base: "AUD",
      quote,
      rate: 1,
      asOf: new Date().toISOString().slice(0, 10),
      source: "identity",
      fetchedAt: new Date().toISOString(),
      manual: false,
    });
  const response = await fetch(
    `/api/fx?currency=${encodeURIComponent(quote)}`,
    { signal, headers: { Accept: "application/json" } },
  );
  if (!response.ok) throw new Error(`FX endpoint failed (${response.status})`);
  const result = freezeFxSnapshot((await response.json()) as FxSnapshot);
  if (result.quote !== quote)
    throw new Error("FX endpoint returned the wrong currency");
  return result;
}
